// The /studio sign-in (MOVORRA-STAFF-1): ONE form for both account systems, so nobody has to know which one they
// are in.
//
//   1. Try the company login (ride-share pool). Any account there with a group is done: App sends a Movorra admin to
//      Studio, an R3 administrator to R3's Studio (Applications + Integration), and a dispatcher or driver to their
//      own app — /studio is the one sign-in link (COMPANY-STUDIO-1).
//   2. Otherwise (no company account, or one with no groups) try the Studio login with the same email + password —
//      for Studio-only accounts.
//   3. A groupless company account with no Studio account is handed back as a company session, so App can say
//      "this account can't open anything here" instead of a misleading password error.
//
// Invited users hit Cognito's new-password step on their first sign-in; it is completed on whichever pool raised it.
//
// Password reset: an account an admin reset (RESET_REQUIRED) fails sign-in with PasswordResetRequiredException and
// Cognito has already emailed a code, so that opens straight on the enter-code step against the pool that raised it.
// "Forgot password?" doesn't know the pool yet: it asks the company login first (as sign-in does) and the Studio
// login only when the company login has no such user.
import React, { useRef, useState } from 'react';
import {
  signIn, completeNewPassword, signOut, requestPasswordReset, confirmPasswordReset,
  signInEcap, completeNewPasswordEcap, requestPasswordResetEcap, confirmPasswordResetEcap,
} from '../services/authService';
import { identityFromClaims } from '../access/permissions';
import { Button, TextInput, Field } from '../ui/primitives';
import { IconLogo } from '../ui/icons';
import PasswordResetForm from './PasswordResetForm';

const NOT_IN_POOL = new Set(['UserNotFoundException', 'NotAuthorizedException']);
const RESET_REQUIRED = 'PasswordResetRequiredException';
const RESET_BY_POOL = {
  company: { request: requestPasswordReset, confirm: confirmPasswordReset },
  studio: { request: requestPasswordResetEcap, confirm: confirmPasswordResetEcap },
};
// A company login with any admin-assigned group has somewhere to go (Studio, R3's Studio, or their own app).
const hasCompanyRole = (session) => identityFromClaims((session && session.user) || {}, 'rideshare').groups.size > 0;

function StudioSignIn({ onCompanyLogin, onStudioLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [challenge, setChallenge] = useState(null); // { pool: 'company' | 'studio', cognitoUser }
  const [reset, setReset] = useState(null); // { step: 'request' | 'confirm', notice } while resetting a password
  const resetPool = useRef(null); // 'company' | 'studio' once the reset knows which login the email is in
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const openResetRequired = (pool) => {
    resetPool.current = pool;
    setReset({
      step: 'confirm',
      notice: 'Your password was reset. Enter the verification code we emailed you (or send a new one) and choose '
        + 'a new password.',
    });
  };

  // Step 2: the Studio login with the same credentials. Resolves true when it handled the sign-in.
  const tryStudio = async (user, pass) => {
    try {
      onStudioLogin(await signInEcap(user, pass));
      return true;
    } catch (err) {
      if (err && err.code === 'NewPasswordRequired') {
        setChallenge({ pool: 'studio', cognitoUser: err.cognitoUser });
        return true;
      }
      if (err && err.code === RESET_REQUIRED) {
        openResetRequired('studio');
        return true;
      }
      if (err && NOT_IN_POOL.has(err.code)) return false;
      throw err;
    }
  };

  const signInWith = async (user, pass) => {
    setError('');
    setLoading(true);
    try {
      let company = null;
      try {
        company = await signIn(user, pass);
      } catch (err) {
        if (err && err.code === 'NewPasswordRequired') {
          setChallenge({ pool: 'company', cognitoUser: err.cognitoUser });
          return;
        }
        if (err && err.code === RESET_REQUIRED) {
          openResetRequired('company');
          return;
        }
        if (!(err && NOT_IN_POOL.has(err.code))) throw err;
      }
      if (company && hasCompanyRole(company)) {
        onCompanyLogin(company);
        return;
      }
      if (await tryStudio(user, pass)) {
        if (company) signOut(); // a company session that Studio did not use must not linger
        return;
      }
      if (company) {
        onCompanyLogin(company); // a company account that can't open Studio — App explains that
        return;
      }
      setError('Incorrect email or password.');
    } catch (err) {
      setError((err && err.message) || 'Sign in failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = (e) => {
    e.preventDefault();
    signInWith(email, password);
  };

  const openReset = () => {
    setError('');
    resetPool.current = null;
    setReset({ step: 'request', notice: '' });
  };

  const closeReset = () => {
    setReset(null);
    setPassword('');
    resetPool.current = null;
  };

  const requestReset = async (user) => {
    if (resetPool.current) return RESET_BY_POOL[resetPool.current].request(user);
    try {
      const destination = await requestPasswordReset(user);
      resetPool.current = 'company';
      return destination;
    } catch (err) {
      if (!(err && err.code === 'UserNotFoundException')) throw err;
      const destination = await requestPasswordResetEcap(user);
      resetPool.current = 'studio';
      return destination;
    }
  };

  const confirmReset = (user, code, pass) => RESET_BY_POOL[resetPool.current || 'company'].confirm(user, code, pass);

  // Reset done — sign in with the new password through the normal flow, so the account lands where sign-in sends it.
  const handleResetDone = async (user, pass) => {
    closeReset();
    setEmail(user);
    setPassword(pass);
    await signInWith(user, pass);
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (challenge.pool === 'company') {
        onCompanyLogin(await completeNewPassword(challenge.cognitoUser, newPassword));
      } else {
        onStudioLogin(await completeNewPasswordEcap(challenge.cognitoUser, newPassword));
      }
    } catch (err) {
      setError((err && err.message) || 'Password change failed.');
    } finally {
      setLoading(false);
    }
  };

  const header = (subtitle) => (
    <div className="flex flex-col items-center text-center mb-6">
      <div className="w-14 h-14 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-sm mb-4">
        <IconLogo size={28} />
      </div>
      <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Movorra Studio</h1>
      <p className="text-sm text-slate-600 leading-relaxed mt-3">{subtitle}</p>
    </div>
  );

  const errorBlock = error ? (
    <div role="alert" className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">
      {error}
    </div>
  ) : null;

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-10 bg-slate-50">
      <div className="w-full max-w-sm bg-white rounded-xl border border-slate-200 shadow-lg p-8 fade-in">
        {reset ? (
          <>
            {header('Reset your password')}
            <PasswordResetForm
              initialUsername={email}
              initialStep={reset.step}
              notice={reset.notice}
              requestReset={requestReset}
              confirmReset={confirmReset}
              onDone={handleResetDone}
              onCancel={closeReset}
            />
          </>
        ) : challenge ? (
          <>
            {header('Set a new password to continue')}
            {errorBlock}
            <form onSubmit={handleNewPassword} className="space-y-3">
              <Field label="New password">
                <TextInput type="password" placeholder="New password" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)} required autoFocus />
              </Field>
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? 'Setting…' : 'Set password'}
              </Button>
            </form>
          </>
        ) : (
          <>
            {header('Sign in with your Movorra account')}
            {errorBlock}
            <form onSubmit={handleSignIn} className="space-y-3">
              <Field label="Email">
                <TextInput type="text" placeholder="you@company.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </Field>
              <Field label="Password">
                <TextInput type="password" placeholder="••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)} required />
              </Field>
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button type="button" className="text-sm text-indigo-600 hover:text-indigo-800" onClick={openReset}>
                Forgot password?
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default StudioSignIn;
